import type { DispatchFilter, DispatchPriority, DispatchQueueItem } from "@/data/dispatchIntelligence";
import type { DerivedJobState, UnifiedFollowUpState, UnifiedInstallState, UnifiedPaymentState } from "@/data/jobStateDerived";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export type SchedulingDispatchRow = DispatchQueueItem;

export interface SchedulingDispatchPageQuery {
  page: number;
  pageSize: number;
  search?: string;
  filter?: DispatchFilter;
  refreshToken?: number;
}

export interface SchedulingDispatchSummary {
  total: number;
  critical: number;
  blocked: number;
  inProgress: number;
  paymentDue: number;
  readyToSchedule: number;
  qaReady: number;
  stalled: number;
}

type SchedulingDispatchViewRow = {
  job_id?: string | null;
  job_number?: string | null;
  job_display_ref?: string | null;
  customer_name?: string | null;
  current_stage?: string | null;
  project_type?: string | null;
  address?: string | null;
  state_install?: UnifiedInstallState | null;
  state_payment?: UnifiedPaymentState | null;
  state_follow_up?: UnifiedFollowUpState | null;
  state_label?: string | null;
  state_summary?: string | null;
  state_can_progress?: boolean | null;
  state_blockers?: string[] | null;
  next_action?: string | null;
  next_action_cta?: string | null;
  next_action_route?: string | null;
  priority?: DispatchPriority | null;
  priority_sort?: number | string | null;
  blocker_reason?: string | null;
  payment_label?: string | null;
  install_label?: string | null;
  stale_days?: number | string | null;
  flags?: string[] | null;
  sort_at?: string | null;
};

type SchedulingDispatchSummaryViewRow = {
  total?: number | string | null;
  critical?: number | string | null;
  blocked?: number | string | null;
  in_progress?: number | string | null;
  payment_due?: number | string | null;
  ready_to_schedule?: number | string | null;
  qa_ready?: number | string | null;
  stalled?: number | string | null;
};

const ROW_COLUMNS = [
  "job_id",
  "job_number",
  "job_display_ref",
  "customer_name",
  "current_stage",
  "project_type",
  "address",
  "state_install",
  "state_payment",
  "state_follow_up",
  "state_label",
  "state_summary",
  "state_can_progress",
  "state_blockers",
  "next_action",
  "next_action_cta",
  "next_action_route",
  "priority",
  "priority_sort",
  "blocker_reason",
  "payment_label",
  "install_label",
  "stale_days",
  "flags",
  "sort_at",
].join(", ");

const SUMMARY_COLUMNS = [
  "total",
  "critical",
  "blocked",
  "in_progress",
  "payment_due",
  "ready_to_schedule",
  "qa_ready",
  "stalled",
].join(", ");

const FILTER_COLUMN: Partial<Record<DispatchFilter, string>> = {
  blocked: "is_blocked",
  in_progress: "is_in_progress",
  payment_required: "is_payment_required",
  ready_to_schedule: "is_ready_to_schedule",
  qa_ready: "is_qa_ready",
  stalled: "is_stalled",
};

function num(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({
    key,
    durationMs: Date.now() - startedAt,
    payloadBytes,
    cacheHit: false,
  });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

function sanitizedSearchPattern(search: string): string {
  return `%${search.trim().replace(/[%_]/g, "").replace(/,/g, " ")}%`;
}

function mapState(row: SchedulingDispatchViewRow): DerivedJobState {
  return {
    install: row.state_install ?? "not_started",
    payment: row.state_payment ?? "no_payment_required",
    followUp: row.state_follow_up ?? "none",
    label: row.state_label ?? "Pre Install",
    summary: row.state_summary ?? "",
    canProgress: row.state_can_progress ?? !(row.state_blockers?.length),
    blockers: Array.isArray(row.state_blockers) ? row.state_blockers.filter(Boolean) : [],
  };
}

function mapRow(row: SchedulingDispatchViewRow): SchedulingDispatchRow {
  const jobId = String(row.job_id ?? "");
  return {
    jobId,
    jobNumber: String(row.job_number ?? jobId),
    jobDisplayRef: String(row.job_display_ref ?? row.job_number ?? jobId),
    customerName: String(row.customer_name ?? "Unknown"),
    currentStage: String(row.current_stage ?? "Sale Closed"),
    projectType: String(row.project_type ?? "Other"),
    address: String(row.address ?? ""),
    state: mapState(row),
    nextAction: String(row.next_action ?? "Review Job"),
    nextActionCta: String(row.next_action_cta ?? "Open Job"),
    nextActionRoute: String(row.next_action_route ?? `/jobs/${jobId}`),
    priority: row.priority ?? "medium",
    blockerReason: row.blocker_reason ?? null,
    paymentLabel: String(row.payment_label ?? "No Payment"),
    installLabel: String(row.install_label ?? "Pre-Install"),
    staleDays: num(row.stale_days),
    flags: Array.isArray(row.flags) ? row.flags.filter(Boolean) : [],
  };
}

function mapSummary(row: SchedulingDispatchSummaryViewRow | null | undefined): SchedulingDispatchSummary {
  return {
    total: num(row?.total),
    critical: num(row?.critical),
    blocked: num(row?.blocked),
    inProgress: num(row?.in_progress),
    paymentDue: num(row?.payment_due),
    readyToSchedule: num(row?.ready_to_schedule),
    qaReady: num(row?.qa_ready),
    stalled: num(row?.stalled),
  };
}

export const SchedulingDispatchPageService = {
  async getRowsPage(query: SchedulingDispatchPageQuery): Promise<PageResponse<SchedulingDispatchRow>> {
    const key = `scheduling-dispatch.rows-page.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        let request = supabase
          .from("v_scheduling_dispatch_jobs_ui")
          .select(ROW_COLUMNS, { count: "exact" })
          .order("priority_sort", { ascending: true })
          .order("sort_at", { ascending: false })
          .order("job_id", { ascending: true })
          .range(from, to);

        const search = query.search?.trim();
        if (search) {
          const pattern = sanitizedSearchPattern(search);
          request = request.or([
            `customer_name.ilike.${pattern}`,
            `job_display_ref.ilike.${pattern}`,
            `next_action.ilike.${pattern}`,
            `project_type.ilike.${pattern}`,
            `current_stage.ilike.${pattern}`,
          ].join(","));
        }

        const filterColumn = query.filter && query.filter !== "all"
          ? FILTER_COLUMN[query.filter]
          : undefined;
        if (filterColumn) request = request.eq(filterColumn, true);

        const { data, error, count } = await request;
        if (error) throw error;
        return {
          data: ((data ?? []) as SchedulingDispatchViewRow[]).map(mapRow),
          count: count ?? 0,
          error: null,
        };
      });
    } catch (error) {
      console.warn("SchedulingDispatchPageService.getRowsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken?: number): Promise<{ data: SchedulingDispatchSummary | null; error: unknown | null }> {
    const key = `scheduling-dispatch.summary.${hashFilters({ refreshToken })}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_scheduling_dispatch_summary")
          .select(SUMMARY_COLUMNS)
          .maybeSingle();
        if (error) throw error;
        return { data: mapSummary(data as SchedulingDispatchSummaryViewRow | null), error: null };
      });
    } catch (error) {
      console.warn("SchedulingDispatchPageService.getSummary failed:", error);
      return { data: null, error };
    }
  },
};
